import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';

import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { calculateBaseUrl, useCalculateMultipleVoucher } from '@/logic/services';
import { useDialog, useOrder, useSnack } from '@/logic/state';

import { LoadingButton } from '@mui/lab';
import { formatPrice } from '@/logic/helpers/format-price';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useQueryClient } from '@tanstack/react-query';

import { CloseButton } from '../shared/close-button';
import ImageBoxFuture from '../shared/image-box-future';

const voucherTicketIcon = '/icons/checkout/voucher-ticket-icon.svg';

type QueryKeyType = {
  url: string;
};

const SubTitle: FC<TypographyProps> = ({ children }) => (
  <Typography
    variant='subtitle1'
    sx={theme => ({
      fontSize: {
        mobile: '12px',
        tablet: pxToRem(16),
        color: theme.palette.grey[50],
      },
    })}
  >
    {children}
  </Typography>
);

const BoldTypo: FC<TypographyProps> = ({ children, sx }) => {
  const theme = useTheme();

  return (
    <Typography
      variant='h6'
      sx={{
        fontSize: {
          mobile: '12px',
          tablet: pxToRem(16),
        },
        fontWeight: 700,
        color: theme.palette.grey[80],
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

type QuantityBtnGroupProps = {
  quantity: number;
  setQuantity: (value: number) => void;
  isCalculating: boolean;
};

const QuantityBtnGroup: FC<QuantityBtnGroupProps> = ({
  quantity,
  setQuantity,
  isCalculating,
}) => {
  const handleIncrease = () => {
    setQuantity(quantity + 1);
  };
  const handleDecrease = () => setQuantity(Math.max(1, quantity - 1));

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      sx={theme => ({
        borderRadius: '12px',
        backgroundColor: theme.palette.lightGrey.main,
        width: pxToRem(130),
        paddingY: '10px',
      })}
    >
      <IconButton
        disabled={isCalculating}
        onClick={handleDecrease}
        sx={theme => ({
          color: theme.palette.error.main,
          width: '40px',
          height: '40px',
        })}
      >
        -
      </IconButton>
      <Box>{quantity}</Box>
      <IconButton
        disabled={isCalculating}
        onClick={handleIncrease}
        sx={theme => ({
          color: theme.palette.success.main,
          width: '40px',
          height: '40px',
        })}
      >
        +
      </IconButton>
    </Stack>
  );
};

export const ApplyMultiVoucherDialog: FC = () => {
  const theme = useTheme();
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [order] = useOrder(s => [s.order], shallow);

  const intl = useIntl();
  const [requiredQuantity, setRequiredQuantity] = useState<number>(1);
  const [actualQuantity, setActualQuantity] = useState<number>(1);

  const [voucherDetails, setVoucherDetails] = useState(
    dialog?.applyMultiVoucherDialogPayload?.initialState
  );

  const queryClient = useQueryClient();

  const handleCloseClick = () => setDialog({});

  const {
    data,
    isFetching: isCalculating,
    isError,
  } = useCalculateMultipleVoucher(
    {
      query: {
        orderId: order?.id,
      },
      params: {
        barCode: dialog?.applyMultiVoucherDialogPayload?.barCode,
        quantity: requiredQuantity,
      },
    },
    {
      staleTime: 60 * 1000,
      refetchOnMount: 'always',
      onError: err => {
        if (requiredQuantity < actualQuantity) {
          setActualQuantity(requiredQuantity);
        } else {
          setRequiredQuantity(actualQuantity);
        }
      },
    }
  );

  useEffect(() => {
    // clear cache after remount
    queryClient.invalidateQueries({
      predicate: query => {
        return (query.queryKey[0] as QueryKeyType)?.url === calculateBaseUrl;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.availableNumOfUse && !isError && !isCalculating) {
      setVoucherDetails(data);
      setActualQuantity(requiredQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isCalculating, isError]);

  return (
    <>
      <DialogTitle>
        <CloseButton handleClick={handleCloseClick} />
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: 'auto',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.grey[90],
              fontSize: pxToRem(22),
              fontWeight: 700,
              lineHeight: '33px',
              marginBottom: pxToRem(24),
            }}
          >
            <FormattedMessage id='voucher.multi.dialog.title' />
          </Typography>

          <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack>
              <Stack direction='row'>
                <ImageBoxFuture
                  img={voucherTicketIcon}
                  title={intl.formatMessage({
                    id: 'alt.voucher_ticketI_icon',
                  })}
                  objectFit='contain'
                  sx={{
                    width: pxToRem(59),
                    height: pxToRem(48),
                  }}
                />
                <BoldTypo sx={{ marginLeft: pxToRem(16) }}>
                  ({order?.numOfSeats}) <FormattedMessage id='voucher.selected_seats' />
                </BoldTypo>
              </Stack>
            </Stack>

            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <SubTitle>
                <FormattedMessage id='voucher.applied' />
              </SubTitle>

              <BoldTypo variant='subtitle1'>
                {formatPrice(voucherDetails?.discountAmount, intl)}
              </BoldTypo>
            </Stack>

            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <SubTitle>
                <FormattedMessage id='voucher.uses_left' />
              </SubTitle>
              <BoldTypo variant='subtitle1'>{voucherDetails?.availableNumOfUse}</BoldTypo>
            </Stack>

            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <BoldTypo>
                <FormattedMessage id='voucher.usage_count_prompt' />
              </BoldTypo>
              <QuantityBtnGroup
                quantity={actualQuantity}
                setQuantity={setRequiredQuantity}
                isCalculating={isCalculating}
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant='contained'
          fullWidth
          loading={isCalculating || dialog?.applyMultiVoucherDialogPayload?.isSubmitting}
          disabled={isCalculating || dialog?.applyMultiVoucherDialogPayload?.isSubmitting}
          onClick={() => {
            dialog?.applyMultiVoucherDialogPayload?.handleSubmitVoucher(actualQuantity);
          }}
          sx={{
            fontSize: '16px',
            height: { tablet: pxToRem(56), mobile: '56px' },
          }}
        >
          <FormattedMessage id='voucher.apply' />
        </LoadingButton>
      </DialogActions>
    </>
  );
};
